/**
 * m3 echo Frontend - Projects React Query Hooks
 *
 * Custom hooks for managing project data with automatic caching,
 * background refetching, and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../services/api';
import { getIdentityScope } from '../stores/useAuthStore';
import type { Project, ProjectDetail as ProjectDetailType, PromptBreakdownData, HeatmapData } from '../types/api';

/**
 * Query key factory for projects
 *
 * Every key below the root carries the identity scope so a cache entry can never
 * be reused across an account or tenant switch. `all` stays unscoped so that
 * broad invalidations keep matching all scopes.
 */
export const projectKeys = {
  all: ['projects'] as const,
  scoped: () => [...projectKeys.all, getIdentityScope()] as const,
  lists: () => [...projectKeys.scoped(), 'list'] as const,
  list: (filters?: Record<string, any>) => [...projectKeys.lists(), { filters }] as const,
  details: () => [...projectKeys.scoped(), 'detail'] as const,
  detail: (id: number) => [...projectKeys.details(), id] as const,
  breakdown: (id: number) => [...projectKeys.detail(id), 'breakdown'] as const,
  heatmap: (id: number, modelName?: string) => [...projectKeys.detail(id), 'heatmap', modelName || 'all'] as const,
};

/**
 * Fetch all projects with optional filters
 */
export const useProjects = (filters?: { search_term_filter?: string; tenant_id?: number }) => {
  return useQuery({
    queryKey: projectKeys.list(filters),
    queryFn: async () => {
      const params: any = { limit: 100 };
      if (filters?.search_term_filter) {
        params.search_term_filter = filters.search_term_filter;
      }
      if (filters?.tenant_id !== undefined) {
        params.tenant_id = filters.tenant_id;
      }
      const response = await projectsApi.getAll(params);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
    gcTime: 300000,   // 5 minutes (formerly cacheTime)
    // Keep previous data while refetching, but never across an identity change
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === getIdentityScope() ? previousData : undefined,
    // Poll every 3s when any project has running/pending jobs
    refetchInterval: (query) => {
      const data = query.state.data as Project[] | undefined;
      if (!data) return false;
      const hasRunning = data.some(p => p.last_job_status === 'running' || p.last_job_status === 'pending');
      return hasRunning ? 3000 : false;
    },
  });
};

/**
 * Fetch a single project by ID with all jobs
 */
export const useProject = (id: number | undefined) => {
  return useQuery({
    queryKey: projectKeys.detail(id!),
    queryFn: async () => {
      const response = await projectsApi.getById(id!);
      return response.data as ProjectDetailType;
    },
    enabled: !!id, // Only fetch if ID is provided
    staleTime: 10000, // 10 seconds
    gcTime: 300000,   // 5 minutes
    // Poll every 3s when any job is running/pending (for DB-based progress without WebSocket)
    refetchInterval: (query) => {
      const data = query.state.data as ProjectDetailType | undefined;
      if (!data?.jobs) return false;
      const hasRunning = data.jobs.some(j => j.status === 'running' || j.status === 'pending');
      return hasRunning ? 3000 : false;
    },
  });
};

/**
 * Fetch prompt breakdown data for a project
 */
export const usePromptBreakdown = (id: number | undefined) => {
  return useQuery({
    queryKey: projectKeys.breakdown(id!),
    queryFn: async () => {
      const response = await projectsApi.getPromptBreakdown(id!);
      return response.data as PromptBreakdownData;
    },
    enabled: !!id,
    staleTime: 30000, // 30 seconds
    gcTime: 300000,   // 5 minutes
  });
};

/**
 * Fetch heatmap data for a project with optional model filter
 */
export const useHeatmapData = (id: number | undefined, modelName?: string) => {
  return useQuery({
    queryKey: projectKeys.heatmap(id!, modelName),
    queryFn: async () => {
      const params: { model_name?: string; limit?: number } = {};
      if (modelName && modelName !== 'all') {
        params.model_name = modelName;
      }
      const response = await projectsApi.getHeatmapData(id!, params);
      return response.data as HeatmapData;
    },
    enabled: !!id,
    staleTime: 30000, // 30 seconds
    gcTime: 300000,   // 5 minutes
  });
};

/**
 * Create a new project
 */
export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, tenant_id }: { data: Parameters<typeof projectsApi.create>[0]; tenant_id?: number }) =>
      projectsApi.create(data, tenant_id),
    onSuccess: () => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
};

/**
 * Delete a project with optimistic updates
 */
export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: number) => projectsApi.delete(projectId),

    // Optimistic update - remove project immediately from UI
    onMutate: async (projectId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: projectKeys.lists() });

      // Snapshot the previous value
      const previousProjects = queryClient.getQueryData(projectKeys.lists());

      // Optimistically update to the new value
      queryClient.setQueriesData({ queryKey: projectKeys.lists() }, (old: any) => {
        if (!Array.isArray(old)) return old;
        return old.filter((project: any) => project.id !== projectId);
      });

      // Return context with previous value
      return { previousProjects };
    },

    // If the mutation fails, rollback to previous value
    onError: (_err, _projectId, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(projectKeys.lists(), context.previousProjects);
      }
    },

    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
};

/**
 * Re-test a project (creates a new job)
 */
export const useRetestProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: number) => projectsApi.retest(projectId),
    onSuccess: (response, projectId) => {
      // Invalidate both the project detail and the projects list
      // so Dashboard picks up the new running job immediately
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      return response.data; // Return the new job data
    },
  });
};

/**
 * Export project data as CSV
 */
export const useExportProjectCSV = () => {
  return useMutation({
    mutationFn: async ({ projectId, projectName }: { projectId: number; projectName: string }) => {
      const response = await projectsApi.exportCSV(projectId);

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `m3echo_project_${projectId}_${projectName.replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response;
    },
  });
};
